import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from "@mui/material";
import { useEffect, useState, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { useParams, useNavigate } from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import Snackbar from "src/components/common/Snackbar";
import { getStyles } from "src/styles/theme";
import { SnackbarType } from "src/types/common";
import {
  getGCADetails,
  updateProfileComprehensive,
  getGCAUserUpdateHistory,
  checkGCAEmailExists,
} from "src/utils/api/gca";
import { sendOtp, otpVerification } from "src/utils/api/signUp";
import { fileUpload } from "src/utils/api/fileupload";
import { getImageById } from "src/utils/api/dashboard";
import { getDocumentTypeMapping } from "src/utils/transformers/gcaProfileTransformer";
import { OtpType } from "src/constants/common";
import { getStates } from "src/utils/api/roles";
import defaultStyles, { StylesClasses } from "./styles";
import { useAppDispatch } from "src/redux/store";
import { updateGCAEntityStatus } from "src/redux/slices/userDetails";
import QueriesDialog from "./QueriesPanel";
import { getMyUnread } from "src/utils/api/adminQuery";

const styles = getStyles<StylesClasses>(defaultStyles);

interface GCAData {
  id: string;
  gcaType: string;
  status: string;
  createdAt: string;
  updatedAt: string;

  // Government Institutions
  nameOfInstitution?: string;
  typeOfInstitution?: string;
  ministry?: string;
  organizationPAN?: string;
  organizationGST?: string;
  institutionalEmail?: string;
  officialContactNumber?: string;
  website?: string;
  address?: string;
  state?: string;
  district?: string;
  pincode?: string;
  repFullName?: string;
  repDesignation?: string;
  repEmail?: string;
  repMobile?: { countryCode: string; number: string };
  repEmpId?: string;

  // PSU
  nameOfPSU?: string;
  ownershipType?: string;
  psuParentCompanyDetails?: string;
  psuSubsidiaryDetails?: string;
  cinNumber?: string;
  gstNumber?: string;
  primaryEmail?: string;
  primaryContactNumber?: string;
  officeAddress?: string;

  // NGO
  nameOfNGO?: string;
  registrationType?: string;
  ngoRegistrationNumber?: string;
  ngoGSTNumber?: string;
  ngoPANNumber?: string;
  ngoDarpanID?: string;
  ngoEmail?: string;
  ngoContactNumber?: string;
  ngoNatureOfWork?: string;

  // Private Company
  companyName?: string;
  companyCIN?: string;
  companyPAN?: string;
  companyGSTNumber?: string;
  companyEmail?: string;
  companyMobileNumber?: string;
  companyParentDetails?: string;
  companySubsidiariesDetails?: string;
  companyNatureOfBusiness?: string;
  companyStatesOfOperation?: string;
  companyIncorporationDate?: string;

  // Public Limited Company
  registeredCompanyName?: string;
  plcCINNumber?: string;
  plcPANNumber?: string;
  plcGSTNumber?: string;
  sebiListingStatus?: string;
  corporateOfficeEmail?: string;
  plcParentCompanyDetails?: string;
  plcSubsidiariesDetails?: string;

  // Philanthropies/Foundations
  foundationName?: string;
  foundationRegistrationNumber?: string;
  foundationPANNumber?: string;
  foundationEmail?: string;
  foundationMobile?: { countryCode: string; number: string };
  foundationLegalStatus?: string;
  foundationFundingSource?: string;

  // Individuals
  individualFullName?: string;
  individualDateOfBirth?: string;
  individualGender?: string;
  individualEmail?: string;
  individualMobile?: { countryCode: string; number: string };
  individualAadhaarNumber?: string;
  individualPANNumber?: string;
  individualOccupation?: string;
  individualOccupationDetails?: string;

  // Registered Group
  groupName?: string;
  groupType?: string;
  groupEmail?: string;
  groupContactNumber?: { countryCode: string; number: string };
  groupRegistrationNumber?: string;
  groupOfficeAddress?: string;
  groupOfficeState?: string;
  groupOfficeDistrict?: string;
  groupOfficePincode?: string;
  groupRepresentativeFullName?: string;
  groupRepresentativeDesignation?: string;
  groupRepresentativeEmail?: string;
  groupRepresentativePhone?: string;
  groupRepresentativeEmployeeId?: string;

  documents?: any[];
}

// Option constants for Select fields
const institutionTypeOptions = [
  { label: "Central Govt.", value: "CENTRAL_GOVT" },
  { label: "State Govt.", value: "STATE_GOVT" },
  { label: "Autonomous Body", value: "AUTONOMOUS_BODY" },
  { label: "Statutory Body", value: "STATUTORY_BODY" },
];

const ownershipTypeOptions = [
  { label: "Central PSU", value: "CENTRAL_PSU" },
  { label: "State PSU", value: "STATE_PSU" },
];

const registrationTypeOptions = [
  { label: "Society", value: "SOCIETY" },
  { label: "Trust", value: "TRUST" },
  { label: "Section 8 Company", value: "SECTION_8_COMPANY" },
];

const natureOfWorkOptions = [
  { label: "Environment", value: "ENVIRONMENT" },
  { label: "Social", value: "SOCIAL" },
  { label: "Plantation", value: "PLANTATION" },
  { label: "Livelihood", value: "LIVELIHOOD" },
  { label: "Others (specify)", value: "OTHERS" },
];

const sebiListingStatusOptions = [
  { label: "NSE", value: "NSE" },
  { label: "BSE", value: "BSE" },
  { label: "Unlisted Public Ltd.", value: "UNLISTED_PUBLIC_LTD" },
];

const legalStatusFoundationOptions = [
  { label: "Society", value: "SOCIETY" },
  { label: "Trust", value: "TRUST" },
  { label: "Non-profit Foundation", value: "NON_PROFIT_FOUNDATION" },
];

const occupationDetailsOptions = [
  { label: "Government", value: "GOVERNMENT" },
  { label: "Private", value: "PRIVATE" },
  { label: "Student", value: "STUDENT" },
  { label: "Self-employed", value: "SELF_EMPLOYED" },
  { label: "Other", value: "OTHER" },
];

const genderOptions = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Others", value: "OTHERS" },
];

const groupTypeOptions = [
  { label: "Self-Help Group (SHGs)", value: "SHG" },
  { label: "Farmer Producer Organizations (FPOs)", value: "FPO" },
  { label: "Village Committee", value: "VILLAGE_COMMITTEE" },
  { label: "Others", value: "OTHER" },
];

// Human-readable labels for document type codes used by the backend.
const DocumentTypeLabels: Record<string, string> = {
  PAN: "PAN",
  GST_CERTIFICATE: "GST Certificate",
  CIN: "Certificate of Incorporation (CIN)",
  AADHAAR: "Aadhaar",
  CERTIFICATE: "Registration / Authorization Certificate",
  TAX_EXEMPTION_CERTIFICATE: "12A / 80G Certificate",
  DARPAN_ID: "Darpan Certificate",
  AUDITED_FINANCIAL_STATEMENTS: "Audited Financial Statements",
  STOCK_EXCHANGE_LISTING: "Stock Exchange Listing",
  CSR_ESG_CERTIFICATE: "CSR / ESG Document",
  QUALIFICATION_CERTIFICATE: "Qualification Certificate",
  EXPERIENCE_CERTIFICATE: "Experience Certificate",
  PARENT_COMPANY_DETAILS: "Parent Company Details",
  SUBSIDIARIES_DETAILS: "Subsidiaries Details",
};

const GCATypeLabels: Record<string, string> = {
  GOVERNMENT_INSTITUTIONS: "Government Institutions",
  PSU: "Public Sector Undertakings (PSUs)",
  NGO: "Non-Governmental Organizations (NGOs)",
  PRIVATE_COMPANY: "Private Companies",
  PUBLIC_LIMITED_COMPANY: "Public Limited Company",
  PHILANTHROPY: "Philanthropies / Foundations",
  INDIVIDUAL: "Individuals",
  REGISTERED_GROUP: "Registered Group of Individuals",
};

// Thumbnail that resolves a fresh pre-signed URL by S3 key (the `serialNo`)
// so previews don't break when the original `frontUrl` has expired. Falls
// back to the stored URL if the lookup fails.
const DocumentPreview = ({
  serialNo,
  fallbackUrl,
}: {
  serialNo?: string;
  fallbackUrl?: string;
}) => {
  const [src, setSrc] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!serialNo) {
        if (!cancelled) setSrc(fallbackUrl || "");
        return;
      }
      try {
        const res: any = await getImageById(serialNo);
        const fresh = res?.data?.fileUrl || res?.data?.data?.fileUrl;
        if (!cancelled) setSrc(fresh || fallbackUrl || "");
      } catch {
        if (!cancelled) setSrc(fallbackUrl || "");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serialNo, fallbackUrl]);

  const isPdf = (src || fallbackUrl || "").toLowerCase().includes(".pdf");

  if (!src && !fallbackUrl) {
    return (
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 1,
          backgroundColor: "#f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9e9e9e",
          fontSize: 11,
        }}
      >
        No file
      </Box>
    );
  }

  if (isPdf) {
    return (
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 1,
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#d32f2f",
          fontWeight: 600,
          fontSize: 14,
          border: "1px solid #eee",
        }}
      >
        PDF
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src || fallbackUrl}
      alt="document preview"
      onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.target as HTMLImageElement;
        target.src = "/assets/svg/receipt.svg";
      }}
      sx={{
        width: 72,
        height: 72,
        objectFit: "cover",
        borderRadius: 1,
        border: "1px solid #eee",
      }}
    />
  );
};

const GCAView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = id || "";
  const [isEditing, setIsEditing] = useState(false);
  const [queriesOpen, setQueriesOpen] = useState(false);

  const { data: queriesUnreadResp } = useQuery(
    ["myAdminQueriesUnread"],
    () => getMyUnread(),
    {
      refetchInterval: 30000,
    }
  );
  const queriesUnreadCount = queriesUnreadResp?.data?.data?.count ?? 0;
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<GCAData | null>(null);
  const [showSnackbar, setShowSnackbar] = useState<SnackbarType>({
    severity: "success",
    message: "",
  });
  const [stateOptions, setStateOptions] = useState([]);

  // Email verification states
  const [emailVerificationStatus, setEmailVerificationStatus] = useState<{
    [key: string]: {
      verified: boolean;
      otp?: string;
      showOtpModal?: boolean;
      otpToken?: string;
      refId?: string;
    };
  }>({});
  const [verifyingEmail, setVerifyingEmail] = useState<string | null>(null);
  const [otpInput, setOtpInput] = useState<{ [key: string]: string }>({});

  // OTP-on-save flow
  const [otpDialog, setOtpDialog] = useState<{
    open: boolean;
    email: string;
    refId: string;
    otpToken: string;
    otp: string;
    sending: boolean;
    verifying: boolean;
    error: string;
  }>({
    open: false,
    email: "",
    refId: "",
    otpToken: "",
    otp: "",
    sending: false,
    verifying: false,
    error: "",
  });
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<any>(null);

  // Debounced availability check for the representative email field. Only one
  // rep email is shown at a time (across all GCA types), so a single slot is
  // enough. `checkedEmail` lets us decide whether the latest typed value
  // matches what we last verified, so the save gate is accurate.
  const [repEmailCheck, setRepEmailCheck] = useState<{
    checking: boolean;
    available: boolean | null;
    checkedEmail: string;
    error: string;
  }>({ checking: false, available: null, checkedEmail: "", error: "" });
  const repEmailCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const repEmailCheckSeqRef = useRef(0);

  // Per-document-type upload progress in the edit view.
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);

  const {
    data: gcaResponse,
    isLoading,
    isError,
    refetch,
  } = useQuery(["gcaDetails", userId], () => getGCADetails(userId), {
    onError: (err: any) => {
      setShowSnackbar({
        severity: "error",
        message: err?.response?.data?.message || "Failed to load GCA details",
      });
    },
    enabled: !!userId,
    refetchOnMount: true,
    staleTime: 0,
  });

  // Fetch states from API
  useQuery(["getStates"], () => getStates(), {
    onSuccess: (data) => {
      setStateOptions(data.data || []);
    },
    refetchOnMount: true,
  });

  // Fetch user update history
  const { data: updateHistoryResponse, isLoading: isHistoryLoading } = useQuery(
    ["updateHistory", userId],
    () => getGCAUserUpdateHistory(userId),
    {
      enabled: !!userId,
      refetchOnMount: true,
    }
  );

  const updateHistory = updateHistoryResponse?.data?.data || [];

  // Get districts for selected state
  const districtOptions = useMemo(() => {
    const selectedStateId = editData?.state;
    const selectedStateObj = stateOptions.find(
      (s: any) => s.id === selectedStateId
    );
    return selectedStateObj?.districts || [];
  }, [editData?.state, stateOptions]);

  // Refetch when userId changes
  useEffect(() => {
    if (userId) {
      refetch();
    }
  }, [userId, refetch]);

  // Map state name to state ID when stateOptions and editData are loaded
  useEffect(() => {
    if (editData && stateOptions.length > 0) {
      const convertedData = convertStateNameToId(editData);

      if (
        convertedData.state !== editData.state ||
        convertedData.district !== editData.district
      ) {
        setEditData(convertedData);
      } else {
      }
    }
  }, [editData?.state, stateOptions]);

  // Extract GCA profile from response and map database fields to component fields
  const getGCATypeKey = (typeValue: string): string => {
    const typeMap: Record<string, string> = {
      TYPE_1: "GOVERNMENT_INSTITUTIONS",
      TYPE_2: "PSU",
      TYPE_3: "NGO",
      TYPE_4: "PRIVATE_COMPANY",
      TYPE_5: "PUBLIC_LIMITED_COMPANY",
      TYPE_6: "PHILANTHROPY",
      TYPE_7: "INDIVIDUAL",
      TYPE_8: "REGISTERED_GROUP",
    };
    return typeMap[typeValue] || typeValue;
  };

  // Helper function to convert state name to ID and district name to ID
  const convertStateNameToId = (data: GCAData | null): GCAData | null => {
    if (!data || !stateOptions.length) return data;

    let updatedData = { ...data };

    // Convert state name to ID
    if (data.state) {
      const isStateNameNotId =
        data.state && !data.state.match(/^[0-9a-f-]{36}$/i);

      if (isStateNameNotId) {
        const stateObj = stateOptions.find((s: any) => s.name === data.state);

        if (stateObj) {
          updatedData.state = stateObj.id;

          // Also convert district name to ID if state is found
          if (data.district && stateObj.districts) {
            const isDistrictNameNotId =
              data.district && !data.district.match(/^[0-9a-f-]{36}$/i);

            if (isDistrictNameNotId) {
              const districtObj = stateObj.districts.find(
                (d: any) => d.name === data.district
              );
              if (districtObj) {
                updatedData.district = districtObj.id;
              }
            }
          }
        }
      }
    }

    return updatedData;
  };

  // Format a date string for display (DD-MM-YYYY). Accepts ISO timestamps
  // ("2026-05-12T18:30:00.000Z") or plain "YYYY-MM-DD" — returns empty string
  // for invalid/missing values so callers fall back to the placeholder "_".
  const formatDateForDisplay = (value: string | undefined | null): string => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value as string;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // Convert any date value to "YYYY-MM-DD" so <input type="date"> accepts it.
  const formatDateForInput = (value: string | undefined | null): string => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value as string;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Helper function to find district name from any district UUID
  const getDistrictName = (districtId: string | undefined): string => {
    if (!districtId) return "—";

    // Search through all states' districts
    for (const state of stateOptions) {
      if (state.districts) {
        const district = state.districts.find((d: any) => d.id === districtId);
        if (district) {
          return district.name;
        }
      }
    }

    // If not found, return the ID as fallback
    return districtId;
  };

  // Transform old user data to match GCA structure based on selected type
  const createGCADataFromOldUser = (
    userData: any,
    gcaType: string
  ): Partial<GCAData> => {
    const baseData = {
      gcaType,
      status: "Pending",
    };

    switch (gcaType) {
      case "GOVERNMENT_INSTITUTIONS":
        return {
          ...baseData,
          nameOfInstitution: userData.firstName || "",
          institutionalEmail: userData.email || "",
          officialContactNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PSU":
        return {
          ...baseData,
          nameOfPSU: userData.firstName || "",
          primaryEmail: userData.email || "",
          primaryContactNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "NGO":
        return {
          ...baseData,
          nameOfNGO: userData.firstName || "",
          ngoEmail: userData.email || "",
          ngoContactNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PRIVATE_COMPANY":
        return {
          ...baseData,
          companyName: userData.firstName || "",
          companyEmail: userData.email || "",
          companyMobileNumber: userData.phoneNumber || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PUBLIC_LIMITED_COMPANY":
        return {
          ...baseData,
          registeredCompanyName: userData.firstName || "",
          corporateOfficeEmail: userData.email || "",
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "PHILANTHROPY":
        return {
          ...baseData,
          foundationName: userData.firstName || "",
          foundationEmail: userData.email || "",
          foundationMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          repFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          repDesignation: userData.designation || "",
          repEmail: userData.email || "",
          repMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "INDIVIDUAL":
        return {
          ...baseData,
          individualFullName: userData.firstName || "",
          individualEmail: userData.email || "",
          individualMobile: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          individualOccupation: userData.designation || "",
          address: userData.address?.addressLine1 || "",
          state: userData.address?.state || "",
          district: userData.address?.district || "",
          pincode: userData.address?.zipCode || "",
        };

      case "REGISTERED_GROUP":
        return {
          ...baseData,
          groupName: userData.firstName || "",
          groupEmail: userData.email || "",
          groupContactNumber: {
            countryCode: userData.countryCode || "+91",
            number: userData.phoneNumber || "",
          },
          groupRepresentativeFullName:
            userData.nameOfAuthorizedPerson || userData.firstName || "",
          groupRepresentativeDesignation: userData.designation || "",
          groupRepresentativeEmail: userData.email || "",
          groupRepresentativePhone: userData.phoneNumber || "",
          groupOfficeAddress: userData.address?.addressLine1 || "",
          groupOfficeState: userData.address?.state || "",
          groupOfficeDistrict: userData.address?.district || "",
          groupOfficePincode: userData.address?.zipCode || "",
        };

      default:
        return baseData;
    }
  };

  const mapGCAData = (apiData: any): GCAData | null => {
    if (!apiData) return null;

    const gcaProfile = apiData.gcaProfile?.[0];

    // If no GCA profile exists, this is an old user - create pre-filled form data
    if (!gcaProfile) {
      // Map entityType UUID to GCA type
      const entityTypeMapping: Record<string, string> = {
        "0d984b49-b70c-449c-b493-505ffa32c115": "GOVERNMENT_INSTITUTIONS",
        "2349ac97-480d-446d-a01b-5fc02d6617cf": "PSU",
        "3cf19c7f-7c90-4d81-b297-43bc8cbfa9da": "NGO",
        "4dc93647-093e-4bc7-a390-3f935b100ff3": "PRIVATE_COMPANY",
        "e2215882-38bd-4bc6-a24e-569a40e54a19": "PUBLIC_LIMITED_COMPANY",
        "9bded480-de7b-42d5-96fd-be91b09c2da5": "PHILANTHROPY",
        "adba6d88-c90d-4551-b041-4669530f0c38": "INDIVIDUAL",
        "cfba641c-9026-4cd3-b0b4-5cde09bf1def": "REGISTERED_GROUP",
      };

      // Get GCA type from entityType UUID
      const gcaType =
        entityTypeMapping[apiData.entityType] || "GOVERNMENT_INSTITUTIONS";

      // Return pre-filled GCA data for old users
      const prefilledData = createGCADataFromOldUser(apiData, gcaType);
      return prefilledData as GCAData;
    }

    return {
      id: gcaProfile.id,
      gcaType: getGCATypeKey(gcaProfile.gca_type),
      status: gcaProfile.gca_status,
      createdAt: gcaProfile.created_at,
      updatedAt: gcaProfile.updated_at,

      // Government Institutions
      nameOfInstitution: gcaProfile.entity_name,
      typeOfInstitution: gcaProfile.govt_institution_type,
      ministry: gcaProfile.ministry_department,
      organizationPAN: gcaProfile.govt_institution_pan,
      organizationGST: gcaProfile.govt_institution_gst,
      institutionalEmail: gcaProfile.govt_institution_email,
      officialContactNumber: gcaProfile.govt_institution_contact_number,
      website: gcaProfile.website,
      address: apiData.address?.addressLine1,
      state: apiData.address?.state,
      district: apiData.address?.district,
      pincode: apiData.address?.zipCode,
      repFullName: gcaProfile.representative_full_name,
      repDesignation: gcaProfile.representative_designation,
      repEmail: gcaProfile.representative_email,
      repMobile: gcaProfile.representative_phone
        ? { countryCode: "+91", number: gcaProfile.representative_phone }
        : undefined,

      repEmpId: gcaProfile.representative_employee_id,

      // PSU
      nameOfPSU: gcaProfile.psu_name,
      ownershipType: gcaProfile.psu_sector,
      psuParentCompanyDetails: gcaProfile.psu_parent_details,
      psuSubsidiaryDetails: gcaProfile.psu_subsidiaries_details,
      cinNumber: gcaProfile.psu_cin_gstin,
      gstNumber: gcaProfile.psu_gst_number,
      primaryEmail: gcaProfile.psu_email,
      primaryContactNumber: gcaProfile.psu_contact_number,
      officeAddress: gcaProfile.psu_headquarters,

      // NGO
      nameOfNGO: gcaProfile.ngo_name,
      registrationType: gcaProfile.ngo_registration_type,
      ngoRegistrationNumber: gcaProfile.ngo_registration_number,
      ngoGSTNumber: gcaProfile.ngo_gst_number,
      ngoPANNumber: gcaProfile.ngo_pan_number,
      ngoDarpanID: gcaProfile.ngo_darpan_id,
      ngoEmail: gcaProfile.ngo_email,
      ngoContactNumber: gcaProfile.ngo_contact_number,
      ngoNatureOfWork: gcaProfile.ngo_nature_of_work,

      // Private Company
      companyName: gcaProfile.company_name,
      companyCIN: gcaProfile.cin_gstin,
      companyPAN: gcaProfile.company_pan,
      companyGSTNumber: gcaProfile.company_gst_number,
      companyEmail: gcaProfile.company_email,
      companyMobileNumber: gcaProfile.company_contact_number,
      companyParentDetails: gcaProfile.company_parent_details,
      companySubsidiariesDetails: gcaProfile.company_subsidiaries_details,
      companyNatureOfBusiness: gcaProfile.company_nature_of_business,
      companyStatesOfOperation: gcaProfile.company_states_of_operation,
      companyIncorporationDate: gcaProfile.company_incorporation_date,

      // Public Limited Company
      registeredCompanyName: gcaProfile.plc_registered_company_name,
      plcCINNumber: gcaProfile.plc_cin_number,
      plcPANNumber: gcaProfile.plc_pan_number,
      plcGSTNumber: gcaProfile.plc_gst_number,
      sebiListingStatus: gcaProfile.plc_sebi_listing_status,
      corporateOfficeEmail: gcaProfile.plc_email,
      plcParentCompanyDetails: gcaProfile.plc_parent_company_details,
      plcSubsidiariesDetails: gcaProfile.plc_subsidiaries_details,

      // Philanthropies/Foundations
      foundationName: gcaProfile.foundation_name,
      foundationRegistrationNumber: gcaProfile.foundation_registration_number,
      foundationPANNumber: gcaProfile.foundation_pan_number,
      foundationEmail: gcaProfile.foundation_email,
      foundationMobile: gcaProfile.foundation_contact_number
        ? { countryCode: "+91", number: gcaProfile.foundation_contact_number }
        : undefined,
      foundationLegalStatus: gcaProfile.foundation_legal_status,
      foundationFundingSource: gcaProfile.foundation_funding_source,

      // Individuals
      individualFullName:
        gcaProfile.individual_full_name ||
        `${gcaProfile.individual_fname || ""} ${
          gcaProfile.individual_lname || ""
        }`.trim(),
      individualDateOfBirth:
        gcaProfile.individual_dob || gcaProfile.individual_date_of_birth,
      individualGender: gcaProfile.individual_gender,
      individualEmail: gcaProfile.individual_email || apiData?.email || "",
      individualMobile: gcaProfile.individual_phone
        ? { countryCode: "+91", number: gcaProfile.individual_phone }
        : apiData?.phoneNumber
        ? {
            countryCode: apiData?.countryCode || "+91",
            number: apiData.phoneNumber,
          }
        : undefined,
      individualAadhaarNumber:
        gcaProfile.individual_aadhaar_number || gcaProfile.individual_aadhaar,
      individualPANNumber:
        gcaProfile.individual_pan_number || gcaProfile.individual_pan,
      individualOccupation: gcaProfile.individual_occupation,
      individualOccupationDetails: gcaProfile.individual_occupation_details,

      // Registered Group
      groupName: gcaProfile.group_name,
      groupType: gcaProfile.group_type,
      groupEmail: gcaProfile.group_email,
      groupContactNumber: gcaProfile.group_contact_number
        ? {
            countryCode: "+91",
            number: gcaProfile.group_contact_number,
          }
        : undefined,
      groupRegistrationNumber: gcaProfile.group_registration_number,
      groupOfficeAddress: gcaProfile.group_office_address,
      groupOfficeState: gcaProfile.group_office_state,
      groupOfficeDistrict: gcaProfile.group_office_district,
      groupOfficePincode: gcaProfile.group_office_pincode,
      groupRepresentativeFullName: gcaProfile.representative_full_name,
      groupRepresentativeDesignation: gcaProfile.representative_designation,
      groupRepresentativeEmail: gcaProfile.representative_email,
      groupRepresentativePhone: gcaProfile.representative_phone,
      groupRepresentativeEmployeeId: gcaProfile.representative_employee_id,

      // Documents
      documents: apiData.documents || [],
    };
  };

  const rawGcaData: GCAData | null =
    mapGCAData(gcaResponse?.data?.data) || null;
  const gcaData: GCAData | null = convertStateNameToId(rawGcaData);

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      setEditData(null);
      setIsEditing(false);
      if (repEmailCheckTimerRef.current) {
        clearTimeout(repEmailCheckTimerRef.current);
        repEmailCheckTimerRef.current = null;
      }
      setRepEmailCheck({
        checking: false,
        available: null,
        checkedEmail: "",
        error: "",
      });
    } else {
      setEditData(JSON.parse(JSON.stringify(gcaData)));
      setIsEditing(true);
    }
  };

  // Basic RFC-ish email shape check — only run an availability lookup once
  // the user has typed something that could plausibly be an email, so we
  // don't spam the API on every keystroke.
  const isLikelyEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // Schedule a debounced availability check for the rep email. Skips the API
  // call when the value matches the email currently on file (no-op change)
  // or isn't email-shaped yet. Uses a sequence counter so a stale response
  // can't overwrite a newer one.
  const scheduleRepEmailCheck = (rawValue: string) => {
    const value = (rawValue || "").trim().toLowerCase();
    const original = (pickRepEmail(gcaData) || "").trim().toLowerCase();

    if (repEmailCheckTimerRef.current) {
      clearTimeout(repEmailCheckTimerRef.current);
      repEmailCheckTimerRef.current = null;
    }

    if (!value) {
      setRepEmailCheck({
        checking: false,
        available: null,
        checkedEmail: "",
        error: "",
      });
      return;
    }

    if (value === original) {
      // Unchanged — no save-time duplicate check will run for this case
      // (handleSave skips when !emailChanged), so don't surface any status.
      setRepEmailCheck({
        checking: false,
        available: null,
        checkedEmail: "",
        error: "",
      });
      return;
    }

    if (!isLikelyEmail(value)) {
      setRepEmailCheck({
        checking: false,
        available: null,
        checkedEmail: "",
        error: "",
      });
      return;
    }

    setRepEmailCheck((prev) => ({ ...prev, checking: true, error: "" }));

    repEmailCheckTimerRef.current = setTimeout(async () => {
      const seq = ++repEmailCheckSeqRef.current;
      try {
        const res: any = await checkGCAEmailExists(value, userId);
        if (seq !== repEmailCheckSeqRef.current) return; // stale
        const exists = !!res?.data?.data?.exists;
        setRepEmailCheck({
          checking: false,
          available: !exists,
          checkedEmail: value,
          error: "",
        });
      } catch (err: any) {
        if (seq !== repEmailCheckSeqRef.current) return;
        setRepEmailCheck({
          checking: false,
          available: null,
          checkedEmail: "",
          error:
            err?.response?.data?.message ||
            "Could not verify email availability",
        });
      }
    }, 500);
  };

  // Handle field change in edit mode
  const handleFieldChange = (field: keyof GCAData, value: any) => {
    if (editData) {
      const newData = {
        ...editData,
        [field]: value,
      };

      setEditData(newData);

      // Mark email as unverified when it's changed (only for repEmail)
      if (field === "repEmail") {
        setEmailVerificationStatus((prev) => ({
          ...prev,
          [field]: { verified: false, otp: undefined, showOtpModal: false },
        }));
      }

      // Rep-email availability is checked as the user types so dupes are
      // caught before save. `groupRepresentativeEmail` is the same field
      // under a different name for the REGISTERED_GROUP type.
      if (field === "repEmail" || field === "groupRepresentativeEmail") {
        scheduleRepEmailCheck(value);
      }
    }
  };

  // Send OTP to email
  const handleSendOTP = async (emailField: string, email: string) => {
    if (!email) {
      setShowSnackbar({
        severity: "error",
        message: "Please enter email first",
      });
      return;
    }

    setVerifyingEmail(emailField);
    try {
      // Call backend API to send OTP
      const response = await sendOtp({ type: "EMAIL", email }, "EMAIL");

      // Store OTP token from response headers
      const otpToken = response?.headers["x-otp-token"];

      setEmailVerificationStatus((prev) => ({
        ...prev,
        [emailField]: {
          verified: false,
          otp: undefined,
          showOtpModal: true,
          otpToken: otpToken,
          refId: response?.data?.data?.refId,
        },
      }));

      setShowSnackbar({
        severity: "success",
        message: `OTP sent to ${email}. Check your inbox.`,
      });
    } catch (err: any) {
      setShowSnackbar({
        severity: "error",
        message: err?.response?.data?.message || "Failed to send OTP",
      });
    } finally {
      setVerifyingEmail(null);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (emailField: string) => {
    const enteredOtp = otpInput[emailField];
    const status = emailVerificationStatus[emailField];

    if (!enteredOtp) {
      setShowSnackbar({
        severity: "error",
        message: "Please enter OTP",
      });
      return;
    }

    try {
      // Call backend API to verify OTP
      await otpVerification(
        {
          refId: status?.refId,
          type: "EMAIL",
          otp: enteredOtp,
        },
        { "x-otp-token": status?.otpToken }
      );

      setEmailVerificationStatus((prev) => ({
        ...prev,
        [emailField]: {
          verified: true,
          otp: undefined,
          showOtpModal: false,
          otpToken: undefined,
          refId: undefined,
        },
      }));
      setOtpInput((prev) => ({
        ...prev,
        [emailField]: "",
      }));
      setShowSnackbar({
        severity: "success",
        message: "Email verified successfully",
      });
    } catch (err: any) {
      setShowSnackbar({
        severity: "error",
        message: err?.response?.data?.message || "Invalid OTP",
      });
    }
  };

  // Validate required fields based on GCA type
  const validateRequiredFields = (data: GCAData): string[] => {
    const errors: string[] = [];

    // Common required fields for all types
    if (!data.state) errors.push("State is required");
    if (!data.district) errors.push("District is required");
    if (!data.pincode) errors.push("Pincode is required");
    if (!data.address) errors.push("Address is required");

    // Type-specific validations
    switch (data.gcaType) {
      case "GOVERNMENT_INSTITUTIONS":
        if (!data.nameOfInstitution)
          errors.push("Name of Institution is required");
        if (!data.typeOfInstitution)
          errors.push("Type of Institution is required");
        if (!data.ministry) errors.push("Ministry/Department is required");
        if (!data.organizationPAN) errors.push("Organization PAN is required");
        if (!data.institutionalEmail)
          errors.push("Institutional Email is required");
        if (!data.officialContactNumber)
          errors.push("Official Contact Number is required");
        if (!data.repFullName)
          errors.push("Representative Full Name is required");
        if (!data.repDesignation)
          errors.push("Representative Designation is required");
        if (!data.repEmail) errors.push("Representative Email is required");
        if (!data.repMobile?.number)
          errors.push("Representative Mobile Number is required");
        break;

      case "PSU":
        if (!data.nameOfPSU) errors.push("Name of PSU is required");
        if (!data.ownershipType) errors.push("Ownership Type is required");
        if (!data.cinNumber) errors.push("CIN Number is required");
        if (!data.gstNumber) errors.push("GST Number is required");
        if (!data.primaryEmail) errors.push("Primary Email is required");
        if (!data.primaryContactNumber)
          errors.push("Primary Contact Number is required");
        break;

      case "NGO":
        if (!data.nameOfNGO) errors.push("Name of NGO is required");
        if (!data.registrationType)
          errors.push("Registration Type is required");
        if (!data.ngoRegistrationNumber)
          errors.push("Registration Number is required");
        if (!data.ngoPANNumber) errors.push("PAN Number is required");
        if (!data.ngoEmail) errors.push("Email is required");
        if (!data.ngoContactNumber) errors.push("Contact Number is required");
        break;

      case "PRIVATE_COMPANY":
        if (!data.companyName) errors.push("Company Name is required");
        if (!data.companyPAN) errors.push("Company PAN is required");
        if (!data.companyGSTNumber)
          errors.push("Company GST Number is required");
        if (!data.companyEmail) errors.push("Company Email is required");
        if (!data.companyMobileNumber)
          errors.push("Company Mobile Number is required");
        break;

      case "PUBLIC_LIMITED_COMPANY":
        if (!data.registeredCompanyName)
          errors.push("Registered Company Name is required");
        if (!data.plcCINNumber) errors.push("CIN Number is required");
        if (!data.plcPANNumber) errors.push("PAN Number is required");
        if (!data.plcGSTNumber) errors.push("GST Number is required");
        if (!data.sebiListingStatus)
          errors.push("SEBI Listing Status is required");
        if (!data.corporateOfficeEmail)
          errors.push("Corporate Office Email is required");
        break;

      case "PHILANTHROPY":
        if (!data.foundationName) errors.push("Foundation Name is required");
        if (!data.foundationRegistrationNumber)
          errors.push("Registration Number is required");
        if (!data.foundationPANNumber) errors.push("PAN Number is required");
        if (!data.foundationEmail) errors.push("Foundation Email is required");
        if (!data.foundationLegalStatus)
          errors.push("Legal Status is required");
        break;

      case "INDIVIDUAL":
        if (!data.individualFullName) errors.push("Full Name is required");
        if (!data.individualDateOfBirth)
          errors.push("Date of Birth is required");
        if (!data.individualGender) errors.push("Gender is required");
        if (!data.individualPANNumber) errors.push("PAN Number is required");
        if (!data.individualAadhaarNumber)
          errors.push("Aadhaar Number is required");
        if (!data.individualEmail) errors.push("Email is required");
        if (!data.individualMobile?.number)
          errors.push("Mobile Number is required");
        break;

      case "REGISTERED_GROUP":
        if (!data.groupName) errors.push("Group Name is required");
        if (!data.groupType) errors.push("Group Type is required");
        if (!data.groupEmail) errors.push("Group Email is required");
        if (!data.groupContactNumber?.number)
          errors.push("Group Contact Number is required");
        if (!data.groupRegistrationNumber)
          errors.push("Registration Number is required");
        break;
    }

    // Every supported document must be uploaded, for every entity type. The
    // set of required documents is derived from the same mapping the upload
    // UI uses, so this stays in sync with what's shown.
    const requiredDocTypes = Array.from(
      new Set(Object.values(getDocumentTypeMapping(data.gcaType)))
    ) as string[];
    const uploadedDocTypes = new Set(
      (Array.isArray(data.documents) ? data.documents : [])
        .filter((d: any) => d?.frontUrl)
        .map((d: any) => d?.type)
    );
    requiredDocTypes.forEach((docType) => {
      if (!uploadedDocTypes.has(docType)) {
        errors.push(`${DocumentTypeLabels[docType] || docType} is required`);
      }
    });

    return errors;
  };

  // Pick the representative-style email for the current entity in a single
  // place so save / change-detection / OTP all agree.
  const pickRepEmail = (data: GCAData | null): string => {
    if (!data) return "";
    return (
      data.repEmail ||
      data.groupRepresentativeEmail ||
      data.individualEmail ||
      data.ngoEmail ||
      data.companyEmail ||
      data.foundationEmail ||
      data.corporateOfficeEmail ||
      data.primaryEmail ||
      data.institutionalEmail ||
      data.groupEmail ||
      ""
    );
  };

  // Props bag for any TextField bound to the rep email — keeps the inline
  // availability status next to every entry point in sync without repeating
  // the conditional logic across 7 GCA-type panels.
  const repEmailFieldStatus = useMemo<{
    error?: boolean;
    helperText?: string;
    FormHelperTextProps?: any;
  }>(() => {
    if (repEmailCheck.checking) {
      return { helperText: "Checking availability…" };
    }
    if (repEmailCheck.error) {
      return { error: true, helperText: repEmailCheck.error };
    }
    if (repEmailCheck.available === false) {
      return {
        error: true,
        helperText: "Email already in use by another account",
      };
    }
    if (repEmailCheck.available === true && repEmailCheck.checkedEmail) {
      return {
        helperText: "Email available",
        FormHelperTextProps: { sx: { color: "success.main" } },
      };
    }
    return {};
  }, [repEmailCheck]);

  // Build the payload the backend expects. Pulled out so we can compute it
  // before sending an OTP and stash it until the OTP is verified.
  const buildUpdatePayload = (data: GCAData) => {
    const repMobile =
      data.repMobile || data.individualMobile || data.groupContactNumber;
    const repEmail = pickRepEmail(data);

    return {
      userData: {
        firstName:
          data.gcaType === "GOVERNMENT_INSTITUTIONS"
            ? data.nameOfInstitution
            : data.gcaType === "PSU"
            ? data.nameOfPSU
            : data.gcaType === "NGO"
            ? data.nameOfNGO
            : data.gcaType === "PRIVATE_COMPANY"
            ? data.companyName
            : data.gcaType === "PUBLIC_LIMITED_COMPANY"
            ? data.registeredCompanyName
            : data.gcaType === "PHILANTHROPY"
            ? data.foundationName
            : data.gcaType === "INDIVIDUAL"
            ? data.individualFullName
            : data.gcaType === "REGISTERED_GROUP"
            ? data.groupName
            : "",
        email: repEmail || "",
        phoneNumber: repMobile?.number || "",
        countryCode: repMobile?.countryCode || "+91",
        designation: data.repDesignation || "",
        nameOfAuthorizedPerson:
          data.repFullName ||
          data.groupRepresentativeFullName ||
          data.individualFullName ||
          "",
      },
      addressData: {
        addressLine1:
          data.address || data.officeAddress || data.groupOfficeAddress || "",
        state: data.state || "",
        district: data.district || data.groupOfficeDistrict || "",
        zipCode: data.pincode || data.groupOfficePincode || "",
        country: "India",
      },
      gcaProfileData: data,
      documents: Array.isArray(data.documents) ? data.documents : [],
    };
  };

  const submitUpdate = async (payload: any) => {
    setIsSaving(true);
    try {
      await updateProfileComprehensive(userId, payload);
      dispatch(updateGCAEntityStatus(true));
      setShowSnackbar({
        severity: "success",
        message: "Profile updated successfully",
      });
      setIsEditing(false);
      setEditData(null);
      setPendingUpdatePayload(null);
      setTimeout(() => navigate("/"), 1500);
    } catch (err: any) {
      setShowSnackbar({
        severity: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update profile",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!editData || !gcaData) return;

    // Validate required fields
    const validationErrors = validateRequiredFields(editData);
    if (validationErrors.length > 0) {
      setShowSnackbar({
        severity: "error",
        message: validationErrors.join(", "),
      });
      return;
    }

    const updatePayload = buildUpdatePayload(editData);
    const newEmail = (updatePayload.userData.email || "").trim().toLowerCase();
    const oldEmail = (pickRepEmail(gcaData) || "").trim().toLowerCase();
    const emailChanged = !!newEmail && newEmail !== oldEmail;

    // Duplicate-email validation is done as the user types
    // (`scheduleRepEmailCheck`); here we just gate on its result so we don't
    // burn an OTP on a save the backend would reject. The check is in flight,
    // unfinished, or known-taken — block.
    if (emailChanged) {
      if (repEmailCheck.checking) {
        setShowSnackbar({
          severity: "error",
          message: "Verifying email availability — try again in a moment",
        });
        return;
      }
      if (repEmailCheck.available === false) {
        setShowSnackbar({
          severity: "error",
          message: "Email already in use by another account",
        });
        return;
      }
      if (
        repEmailCheck.available !== true ||
        repEmailCheck.checkedEmail !== newEmail
      ) {
        // Haven't verified this exact value yet — fall back to a one-shot
        // check so a paste-and-save still works.
        setIsSaving(true);
        try {
          const dupRes: any = await checkGCAEmailExists(newEmail, userId);
          if (dupRes?.data?.data?.exists) {
            setRepEmailCheck({
              checking: false,
              available: false,
              checkedEmail: newEmail,
              error: "",
            });
            setShowSnackbar({
              severity: "error",
              message: "Email already in use by another account",
            });
            return;
          }
          setRepEmailCheck({
            checking: false,
            available: true,
            checkedEmail: newEmail,
            error: "",
          });
        } catch (err: any) {
          setShowSnackbar({
            severity: "error",
            message:
              err?.response?.data?.message ||
              "Unable to verify email availability. Please try again.",
          });
          return;
        } finally {
          setIsSaving(false);
        }
      }
    }

    // OTP goes to the new email if it changed (proves ownership of the new
    // address); otherwise to the existing email on file.
    const otpEmail = emailChanged ? newEmail : oldEmail;

    if (!otpEmail) {
      setShowSnackbar({
        severity: "error",
        message:
          "No email on file to send a verification OTP. Please contact support.",
      });
      return;
    }

    setPendingUpdatePayload(updatePayload);
    setOtpDialog({
      open: true,
      email: otpEmail,
      refId: "",
      otpToken: "",
      otp: "",
      sending: true,
      verifying: false,
      error: "",
    });

    try {
      const res: any = await sendOtp(
        { email: otpEmail, type: OtpType.EMAIL },
        OtpType.EMAIL
      );
      setOtpDialog((prev) => ({
        ...prev,
        sending: false,
        refId: res?.data?.data?.refId || "",
        otpToken: res?.headers?.["x-otp-token"] || "",
      }));
    } catch (err: any) {
      setOtpDialog((prev) => ({
        ...prev,
        sending: false,
        error:
          err?.response?.data?.message ||
          "Failed to send OTP. Please try again.",
      }));
    }
  };

  const handleResendOtp = async () => {
    if (!otpDialog.email) return;
    setOtpDialog((prev) => ({ ...prev, sending: true, error: "" }));
    try {
      const res: any = await sendOtp(
        { email: otpDialog.email, type: OtpType.EMAIL },
        OtpType.EMAIL
      );
      setOtpDialog((prev) => ({
        ...prev,
        sending: false,
        otp: "",
        refId: res?.data?.data?.refId || "",
        otpToken: res?.headers?.["x-otp-token"] || "",
      }));
    } catch (err: any) {
      setOtpDialog((prev) => ({
        ...prev,
        sending: false,
        error:
          err?.response?.data?.message ||
          "Failed to resend OTP. Please try again.",
      }));
    }
  };

  const handleVerifyOtpAndSave = async () => {
    // Email OTPs are auth'd via the `x-otp-token` header — the backend does
    // not return a refId for them (refId is only used for Aadhaar). Gate on
    // otpToken instead so the button doesn't stay disabled forever.
    if (!otpDialog.otp || !otpDialog.otpToken) return;
    setOtpDialog((prev) => ({ ...prev, verifying: true, error: "" }));
    try {
      await otpVerification(
        {
          refId: otpDialog.refId,
          type: OtpType.EMAIL,
          otp: otpDialog.otp,
        },
        { "x-otp-token": otpDialog.otpToken }
      );
      setOtpDialog((prev) => ({ ...prev, open: false, verifying: false }));
      if (pendingUpdatePayload) {
        await submitUpdate(pendingUpdatePayload);
      }
    } catch (err: any) {
      setOtpDialog((prev) => ({
        ...prev,
        verifying: false,
        error: err?.response?.data?.message || "Invalid OTP. Please try again.",
      }));
    }
  };

  // Supported document type codes for the current GCA entity, derived from
  // the same mapping used during registration so view/update stays in sync.
  const supportedDocumentTypes = useMemo(() => {
    if (!gcaData?.gcaType) return [] as string[];
    const mapping = getDocumentTypeMapping(gcaData.gcaType);
    return Array.from(new Set(Object.values(mapping))) as string[];
  }, [gcaData?.gcaType]);

  // Stored `frontUrl` is a pre-signed S3 URL that expires (~24h). Re-fetch a
  // fresh URL by the S3 key (stored as `serialNo`) and open it in a new tab.
  const handleViewDocument = async (doc: any) => {
    const fallbackUrl = doc?.frontUrl;
    const key = doc?.serialNo;
    if (!key && !fallbackUrl) return;
    if (!key) {
      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const res: any = await getImageById(key);
      const fresh = res?.data?.fileUrl || res?.data?.data?.fileUrl;
      if (fresh) {
        window.open(fresh, "_blank", "noopener,noreferrer");
      } else if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err: any) {
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      } else {
        setShowSnackbar({
          severity: "error",
          message: "Unable to open document. Please try again.",
        });
      }
    }
  };

  const handleViewBack = async (doc: any) => {
    const fallbackUrl = doc?.backUrl;
    const key = doc?.backKey || doc?.serialNo;
    if (!key && !fallbackUrl) return;
    if (!key) {
      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const res: any = await getImageById(key);
      const fresh = res?.data?.fileUrl || res?.data?.data?.fileUrl;
      if (fresh) {
        window.open(fresh, "_blank", "noopener,noreferrer");
      } else if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err: any) {
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      }
    }
  };

  // Upload a file for a given document type. On success, replace (by type) or
  // append in editData.documents so the comprehensive update sends it through.
  const handleDocumentUpload = async (docType: string, file: File) => {
    if (!editData) return;
    setUploadingDocType(docType);
    try {
      const form = new FormData();
      form.append("file", file);
      const res: any = await fileUpload(form as any);
      const uploaded = res?.data?.data?.uploadedFile;
      if (!uploaded) {
        throw new Error("Upload response missing file metadata");
      }
      const newDoc = {
        serialNo: uploaded.key || `${docType}-${Date.now()}`,
        frontUrl: uploaded.fileUrl || uploaded.key || "",
        backUrl: null,
        type: docType,
        isVerified: false,
        validFrom: null,
        validTill: null,
      };
      const existing = Array.isArray(editData.documents)
        ? [...editData.documents]
        : [];
      const idx = existing.findIndex((d: any) => d?.type === docType);
      if (idx >= 0) {
        existing[idx] = { ...existing[idx], ...newDoc };
      } else {
        existing.push(newDoc);
      }
      setEditData({ ...editData, documents: existing });
      setShowSnackbar({
        severity: "success",
        message: `${DocumentTypeLabels[docType] || docType} uploaded`,
      });
    } catch (err: any) {
      setShowSnackbar({
        severity: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Document upload failed",
      });
    } finally {
      setUploadingDocType(null);
    }
  };

  const handleCloseOtpDialog = () => {
    setOtpDialog({
      open: false,
      email: "",
      refId: "",
      otpToken: "",
      otp: "",
      sending: false,
      verifying: false,
      error: "",
    });
    setPendingUpdatePayload(null);
  };

  // Handle snackbar reset
  const handleResetSnackbar = () => {
    setShowSnackbar({ severity: "success", message: "" });
  };

  const renderGovernmentInstitutions = (
    data: GCAData,
    editMode: boolean = false
  ) => {
    const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
      <span>
        {children}
        {editMode && <span style={{ color: "red", marginLeft: "4px" }}>*</span>}
      </span>
    );

    return (
      <>
        <Box {...styles("section")}>
          <Typography {...styles("sectionTitle")}>
            Basic Institution Details
          </Typography>
          <Box {...styles("container")}>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>
                <RequiredLabel>Name of Institution</RequiredLabel>
              </Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.nameOfInstitution || ""}
                  onChange={(e) =>
                    handleFieldChange("nameOfInstitution", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.nameOfInstitution || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>
                <RequiredLabel>Type of Institution</RequiredLabel>
              </Typography>
              {editMode ? (
                <Select
                  size="small"
                  fullWidth
                  value={editData?.typeOfInstitution || ""}
                  onChange={(e) =>
                    handleFieldChange("typeOfInstitution", e.target.value)
                  }
                >
                  <MenuItem value="">Select type of institution</MenuItem>
                  {institutionTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography {...styles("value")}>
                  {institutionTypeOptions.find(
                    (o) => o.value === data.typeOfInstitution
                  )?.label ||
                    data.typeOfInstitution ||
                    "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Ministry</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.ministry || ""}
                  onChange={(e) =>
                    handleFieldChange("ministry", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.ministry || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Organization PAN</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.organizationPAN || ""}
                  onChange={(e) =>
                    handleFieldChange("organizationPAN", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.organizationPAN || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Organization GST</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.organizationGST || ""}
                  onChange={(e) =>
                    handleFieldChange("organizationGST", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.organizationGST || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Website</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.website || ""}
                  onChange={(e) => handleFieldChange("website", e.target.value)}
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.website || "_"}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box {...styles("section")}>
          <Typography {...styles("sectionTitle")}>Contact Details</Typography>
          <Box {...styles("container")}>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>
                <RequiredLabel>Institutional Email</RequiredLabel>
              </Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  type="email"
                  value={editData?.institutionalEmail || ""}
                  onChange={(e) =>
                    handleFieldChange("institutionalEmail", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.institutionalEmail || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>
                Official Contact Number
              </Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.officialContactNumber || ""}
                  onChange={(e) =>
                    handleFieldChange("officialContactNumber", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.officialContactNumber || "_"}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box {...styles("section")}>
          <Typography {...styles("sectionTitle")}>
            Registered Address
          </Typography>
          <Box {...styles("container")}>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Address</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.address || ""}
                  onChange={(e) => handleFieldChange("address", e.target.value)}
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.address || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>State</Typography>
              {editMode ? (
                <Select
                  size="small"
                  fullWidth
                  value={editData?.state || ""}
                  onChange={(e) => {
                    // Update both state and district together to avoid stale closure issues
                    const newState = e.target.value;
                    if (editData) {
                      setEditData({
                        ...editData,
                        state: newState,
                        district: "", // Reset district when state changes
                      });
                    }
                  }}
                >
                  <MenuItem value="">Select state</MenuItem>
                  {stateOptions.map((state: any) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography {...styles("value")}>
                  {stateOptions.find((s: any) => s.id === data.state)?.name ||
                    data.state ||
                    "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>District</Typography>
              {editMode ? (
                <Select
                  size="small"
                  fullWidth
                  value={editData?.district || ""}
                  onChange={(e) =>
                    handleFieldChange("district", e.target.value)
                  }
                  disabled={!editData?.state}
                >
                  <MenuItem value="">Select district</MenuItem>
                  {districtOptions.map((district: any) => (
                    <MenuItem key={district.id} value={district.id}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography {...styles("value")}>
                  {getDistrictName(data.district)}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Pincode</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.pincode || ""}
                  onChange={(e) => handleFieldChange("pincode", e.target.value)}
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.pincode || "_"}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box {...styles("section")}>
          <Typography {...styles("sectionTitle")}>
            Authorized Representative Details
          </Typography>
          <Box {...styles("container")}>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Full Name</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.repFullName || ""}
                  onChange={(e) =>
                    handleFieldChange("repFullName", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.repFullName || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Designation</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.repDesignation || ""}
                  onChange={(e) =>
                    handleFieldChange("repDesignation", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.repDesignation || "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>
                <RequiredLabel>Email</RequiredLabel>
              </Typography>
              {editMode ? (
                <>
                  <Box
                    display="flex"
                    gap="10px"
                    alignItems="center"
                    flexWrap="wrap"
                    mb={2}
                    width="100%"
                  >
                    <Box flex={1} minWidth="250px">
                      <TextField
                        size="small"
                        fullWidth
                        type="email"
                        value={editData?.repEmail || ""}
                        onChange={(e) =>
                          handleFieldChange("repEmail", e.target.value)
                        }
                        {...repEmailFieldStatus}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleSendOTP("repEmail", editData?.repEmail || "")
                      }
                      disabled={
                        verifyingEmail === "repEmail" ||
                        emailVerificationStatus["repEmail"]?.verified
                      }
                      sx={{ whiteSpace: "nowrap", minWidth: "80px", mt: 1 }}
                    >
                      {emailVerificationStatus["repEmail"]?.verified
                        ? " Verified"
                        : "Verify"}
                    </Button>
                  </Box>

                  {/* OTP Verification Section */}
                  {emailVerificationStatus["repEmail"]?.showOtpModal && (
                    <Box
                      display="flex"
                      gap="10px"
                      alignItems="center"
                      mb={2}
                      flexWrap="wrap"
                      width="100%"
                      sx={{ backgroundColor: "#f5f5f5", p: 2, borderRadius: 1 }}
                    >
                      <TextField
                        label="Enter OTP"
                        size="small"
                        placeholder="6-digit OTP"
                        value={otpInput["repEmail"] || ""}
                        onChange={(e) =>
                          setOtpInput((prev) => ({
                            ...prev,
                            repEmail: e.target.value,
                          }))
                        }
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleVerifyOTP("repEmail")}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Verify OTP
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Typography {...styles("value")}>
                  {data.repEmail || "_"}
                  {emailVerificationStatus["repEmail"]?.verified && (
                    <Chip
                      label="Verified"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Mobile</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.repMobile?.number || ""}
                  onChange={(e) =>
                    handleFieldChange("repMobile", {
                      ...editData?.repMobile,
                      number: e.target.value,
                    })
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.repMobile
                    ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                    : "_"}
                </Typography>
              )}
            </Box>
            <Box {...styles("input")}>
              <Typography {...styles("label")}>Employee Id</Typography>
              {editMode ? (
                <TextField
                  size="small"
                  fullWidth
                  value={editData?.repEmpId || ""}
                  onChange={(e) =>
                    handleFieldChange("repEmpId", e.target.value)
                  }
                />
              ) : (
                <Typography {...styles("value")}>
                  {data.repEmpId || "_"}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  const renderPSU = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          PSU Organization Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Name of PSU</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.nameOfPSU || ""}
                onChange={(e) => handleFieldChange("nameOfPSU", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.nameOfPSU || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Ownership Type</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.ownershipType || ""}
                onChange={(e) =>
                  handleFieldChange("ownershipType", e.target.value)
                }
              >
                <MenuItem value="">Select ownership type</MenuItem>
                {ownershipTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {ownershipTypeOptions.find(
                  (o) => o.value === data.ownershipType
                )?.label ||
                  data.ownershipType ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Parent Company details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.psuParentCompanyDetails || ""}
                onChange={(e) =>
                  handleFieldChange("psuParentCompanyDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.psuParentCompanyDetails || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Subsidiaries details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.psuSubsidiaryDetails || ""}
                onChange={(e) =>
                  handleFieldChange("psuSubsidiaryDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.psuSubsidiaryDetails || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>CIN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.cinNumber || ""}
                onChange={(e) => handleFieldChange("cinNumber", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.cinNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.gstNumber || ""}
                onChange={(e) => handleFieldChange("gstNumber", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.gstNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Primary Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.primaryEmail || ""}
                onChange={(e) =>
                  handleFieldChange("primaryEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.primaryEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Primary Contact Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.primaryContactNumber || ""}
                onChange={(e) =>
                  handleFieldChange("primaryContactNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.primaryContactNumber || "_"}
              </Typography>
            )}
          </Box>
          {/* <Box {...styles("input")}>
            <Typography {...styles("label")}>Office Address</Typography>
            <Typography {...styles("value")}>
              {data.officeAddress || "_"}
            </Typography>
          </Box> */}
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  const newState = e.target.value;
                  if (editData) {
                    setEditData({ ...editData, state: newState, district: "" });
                  }
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {getDistrictName(data.district)}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
                {...repEmailFieldStatus}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderNGO = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>NGO Details</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Name of NGO</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.nameOfNGO || ""}
                onChange={(e) => handleFieldChange("nameOfNGO", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.nameOfNGO || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Registration Type</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.registrationType || ""}
                onChange={(e) =>
                  handleFieldChange("registrationType", e.target.value)
                }
              >
                <MenuItem value="">Select registration type</MenuItem>
                {registrationTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {registrationTypeOptions.find(
                  (o) => o.value === data.registrationType
                )?.label ||
                  data.registrationType ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>
              NGO Registration Number
            </Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoRegistrationNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoRegistrationNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoRegistrationNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>NGO PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoPANNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>NGO GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoGSTNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoGSTNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoGSTNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Darpan ID</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoDarpanID || ""}
                onChange={(e) =>
                  handleFieldChange("ngoDarpanID", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoDarpanID || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.ngoEmail || ""}
                onChange={(e) => handleFieldChange("ngoEmail", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Contact Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.ngoContactNumber || ""}
                onChange={(e) =>
                  handleFieldChange("ngoContactNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.ngoContactNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Nature of Work</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.ngoNatureOfWork || ""}
                onChange={(e) =>
                  handleFieldChange("ngoNatureOfWork", e.target.value)
                }
              >
                <MenuItem value="">Select nature of work</MenuItem>
                {natureOfWorkOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {natureOfWorkOptions.find(
                  (o) => o.value === data.ngoNatureOfWork
                )?.label ||
                  data.ngoNatureOfWork ||
                  "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  const newState = e.target.value;
                  if (editData) {
                    setEditData({ ...editData, state: newState, district: "" });
                  }
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {getDistrictName(data.district)}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
                {...repEmailFieldStatus}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderPrivateCompany = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Private Company Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyName || ""}
                onChange={(e) =>
                  handleFieldChange("companyName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company CIN</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyCIN || ""}
                onChange={(e) =>
                  handleFieldChange("companyCIN", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyCIN || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company PAN</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyPAN || ""}
                onChange={(e) =>
                  handleFieldChange("companyPAN", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyPAN || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyGSTNumber || ""}
                onChange={(e) =>
                  handleFieldChange("companyGSTNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyGSTNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.companyEmail || ""}
                onChange={(e) =>
                  handleFieldChange("companyEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Company Mobile Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyMobileNumber || ""}
                onChange={(e) =>
                  handleFieldChange("companyMobileNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyMobileNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Parent Company details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.companyParentDetails || ""}
                onChange={(e) =>
                  handleFieldChange("companyParentDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyParentDetails || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Subsidiaries details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.companySubsidiariesDetails || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "companySubsidiariesDetails",
                    e.target.value
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companySubsidiariesDetails || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Nature of Business</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyNatureOfBusiness || ""}
                onChange={(e) =>
                  handleFieldChange("companyNatureOfBusiness", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyNatureOfBusiness || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>States of Operation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.companyStatesOfOperation || ""}
                onChange={(e) =>
                  handleFieldChange("companyStatesOfOperation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.companyStatesOfOperation || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Incorporation Date</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formatDateForInput(editData?.companyIncorporationDate)}
                onChange={(e) =>
                  handleFieldChange("companyIncorporationDate", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {formatDateForDisplay(data.companyIncorporationDate) || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  const newState = e.target.value;
                  if (editData) {
                    setEditData({ ...editData, state: newState, district: "" });
                  }
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {getDistrictName(data.district)}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
                {...repEmailFieldStatus}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderPublicLimitedCompany = (
    data: GCAData,
    editMode: boolean = false
  ) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Public Limited Company Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>
              Registered Company Name
            </Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.registeredCompanyName || ""}
                onChange={(e) =>
                  handleFieldChange("registeredCompanyName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.registeredCompanyName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>CIN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.plcCINNumber || ""}
                onChange={(e) =>
                  handleFieldChange("plcCINNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcCINNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.plcPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("plcPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcPANNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>GST Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.plcGSTNumber || ""}
                onChange={(e) =>
                  handleFieldChange("plcGSTNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcGSTNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>SEBI Listing Status</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.sebiListingStatus || ""}
                onChange={(e) =>
                  handleFieldChange("sebiListingStatus", e.target.value)
                }
              >
                <MenuItem value="">Select SEBI listing status</MenuItem>
                {sebiListingStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {sebiListingStatusOptions.find(
                  (o) => o.value === data.sebiListingStatus
                )?.label ||
                  data.sebiListingStatus ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Corporate Office Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.corporateOfficeEmail || ""}
                onChange={(e) =>
                  handleFieldChange("corporateOfficeEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.corporateOfficeEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Parent Company details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.plcParentCompanyDetails || ""}
                onChange={(e) =>
                  handleFieldChange("plcParentCompanyDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcParentCompanyDetails || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Subsidiaries details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editData?.plcSubsidiariesDetails || ""}
                onChange={(e) =>
                  handleFieldChange("plcSubsidiariesDetails", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.plcSubsidiariesDetails || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  const newState = e.target.value;
                  if (editData) {
                    setEditData({ ...editData, state: newState, district: "" });
                  }
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {getDistrictName(data.district)}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
                {...repEmailFieldStatus}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderPhilanthropy = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Philanthropy/Foundation Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Foundation Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationName || ""}
                onChange={(e) =>
                  handleFieldChange("foundationName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Legal Status</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.foundationLegalStatus || ""}
                onChange={(e) =>
                  handleFieldChange("foundationLegalStatus", e.target.value)
                }
              >
                <MenuItem value="">Select legal status</MenuItem>
                {legalStatusFoundationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {legalStatusFoundationOptions.find(
                  (o) => o.value === data.foundationLegalStatus
                )?.label ||
                  data.foundationLegalStatus ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Registration Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationRegistrationNumber || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "foundationRegistrationNumber",
                    e.target.value
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationRegistrationNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("foundationPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationPANNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Funding Source</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationFundingSource || ""}
                onChange={(e) =>
                  handleFieldChange("foundationFundingSource", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationFundingSource || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.foundationEmail || ""}
                onChange={(e) =>
                  handleFieldChange("foundationEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.foundationMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("foundationMobile", {
                    ...editData?.foundationMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.foundationMobile
                  ? `${data.foundationMobile.countryCode} ${data.foundationMobile.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData.state || ""}
                onChange={(e) => {
                  const newState = e.target.value;
                  if (editData) {
                    setEditData({ ...editData, state: newState, district: "" });
                  }
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {getDistrictName(data.district)}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repFullName || ""}
                onChange={(e) =>
                  handleFieldChange("repFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repFullName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repDesignation || ""}
                onChange={(e) =>
                  handleFieldChange("repDesignation", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repDesignation || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.repEmail || ""}
                onChange={(e) => handleFieldChange("repEmail", e.target.value)}
                {...repEmailFieldStatus}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("repMobile", {
                    ...editData?.repMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repMobile
                  ? `${data.repMobile.countryCode} ${data.repMobile.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee Id</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.repEmpId || ""}
                onChange={(e) => handleFieldChange("repEmpId", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.repEmpId || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderIndividual = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Personal Details</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualFullName || ""}
                onChange={(e) =>
                  handleFieldChange("individualFullName", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualFullName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Date of Birth</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formatDateForInput(editData?.individualDateOfBirth)}
                onChange={(e) =>
                  handleFieldChange("individualDateOfBirth", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {formatDateForDisplay(data.individualDateOfBirth) || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Gender</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.individualGender || ""}
                onChange={(e) =>
                  handleFieldChange("individualGender", e.target.value)
                }
              >
                <MenuItem value="">Select Gender</MenuItem>
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {genderOptions.find((o) => o.value === data.individualGender)
                  ?.label ||
                  data.individualGender ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Aadhaar Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualAadhaarNumber || ""}
                onChange={(e) =>
                  handleFieldChange("individualAadhaarNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualAadhaarNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>PAN Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualPANNumber || ""}
                onChange={(e) =>
                  handleFieldChange("individualPANNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualPANNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.individualEmail || ""}
                onChange={(e) =>
                  handleFieldChange("individualEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Mobile</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualMobile?.number || ""}
                onChange={(e) =>
                  handleFieldChange("individualMobile", {
                    ...editData?.individualMobile,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualMobile
                  ? `${data.individualMobile.countryCode} ${data.individualMobile.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Occupation</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.individualOccupation || ""}
                onChange={(e) =>
                  handleFieldChange("individualOccupation", e.target.value)
                }
              >
                <MenuItem value="">Select Occupation</MenuItem>
                {occupationDetailsOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {occupationDetailsOptions.find(
                  (o) => o.value === data.individualOccupation
                )?.label ||
                  data.individualOccupation ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Occupation Details</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.individualOccupationDetails || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "individualOccupationDetails",
                    e.target.value
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.individualOccupationDetails || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Registered Address</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  const newState = e.target.value;
                  if (editData) {
                    setEditData({ ...editData, state: newState, district: "" });
                  }
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {getDistrictName(data.district)}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderRegisteredGroup = (data: GCAData, editMode: boolean = false) => (
    <>
      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>Group Details</Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Group Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupName || ""}
                onChange={(e) => handleFieldChange("groupName", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Group Type</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.groupType || ""}
                onChange={(e) => handleFieldChange("groupType", e.target.value)}
              >
                <MenuItem value="">Select Group Type</MenuItem>
                {groupTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {groupTypeOptions.find((o) => o.value === data.groupType)
                  ?.label ||
                  data.groupType ||
                  "_"}
              </Typography>
            )}
          </Box>

          <Box {...styles("input")}>
            <Typography {...styles("label")}>Registration Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRegistrationNumber || ""}
                onChange={(e) =>
                  handleFieldChange("groupRegistrationNumber", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRegistrationNumber || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.groupEmail || ""}
                onChange={(e) =>
                  handleFieldChange("groupEmail", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Contact Number</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupContactNumber?.number || ""}
                onChange={(e) =>
                  handleFieldChange("groupContactNumber", {
                    ...editData?.groupContactNumber,
                    number: e.target.value,
                  })
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupContactNumber
                  ? `${data.groupContactNumber.countryCode} ${data.groupContactNumber.number}`
                  : "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Office Address Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Address</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.address || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>State</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.state || ""}
                onChange={(e) => {
                  const newState = e.target.value;
                  if (editData) {
                    setEditData({ ...editData, state: newState, district: "" });
                  }
                }}
              >
                <MenuItem value="">Select state</MenuItem>
                {stateOptions.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {stateOptions.find((s: any) => s.id === data.state)?.name ||
                  data.state ||
                  "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>District</Typography>
            {editMode ? (
              <Select
                size="small"
                fullWidth
                value={editData?.district || ""}
                onChange={(e) => handleFieldChange("district", e.target.value)}
                disabled={!editData?.state}
              >
                <MenuItem value="">Select district</MenuItem>
                {districtOptions.map((district: any) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography {...styles("value")}>
                {getDistrictName(data.district)}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Pincode</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.pincode || ""}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.pincode || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles("section")}>
        <Typography {...styles("sectionTitle")}>
          Authorized Representative Details
        </Typography>
        <Box {...styles("container")}>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Full Name</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativeFullName || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "groupRepresentativeFullName",
                    e.target.value
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeFullName || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Designation</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativeDesignation || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "groupRepresentativeDesignation",
                    e.target.value
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeDesignation || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Email</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                type="email"
                value={editData?.groupRepresentativeEmail || ""}
                onChange={(e) =>
                  handleFieldChange("groupRepresentativeEmail", e.target.value)
                }
                {...repEmailFieldStatus}
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeEmail || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Phone</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativePhone || ""}
                onChange={(e) =>
                  handleFieldChange("groupRepresentativePhone", e.target.value)
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativePhone || "_"}
              </Typography>
            )}
          </Box>
          <Box {...styles("input")}>
            <Typography {...styles("label")}>Employee ID</Typography>
            {editMode ? (
              <TextField
                size="small"
                fullWidth
                value={editData?.groupRepresentativeEmployeeId || ""}
                onChange={(e) =>
                  handleFieldChange(
                    "groupRepresentativeEmployeeId",
                    e.target.value
                  )
                }
              />
            ) : (
              <Typography {...styles("value")}>
                {data.groupRepresentativeEmployeeId || "_"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderGCATypeContent = (data: GCAData, editMode: boolean = false) => {
    switch (data.gcaType) {
      case "GOVERNMENT_INSTITUTIONS":
        return renderGovernmentInstitutions(data, editMode);
      case "PSU":
        return renderPSU(data, editMode);
      case "NGO":
        return renderNGO(data, editMode);
      case "PRIVATE_COMPANY":
        return renderPrivateCompany(data, editMode);
      case "PUBLIC_LIMITED_COMPANY":
        return renderPublicLimitedCompany(data, editMode);
      case "PHILANTHROPY":
        return renderPhilanthropy(data, editMode);
      case "INDIVIDUAL":
        return renderIndividual(data, editMode);
      case "REGISTERED_GROUP":
        return renderRegisteredGroup(data, editMode);
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      APPROVED: "#4caf50",
      REJECTED: "#f44336",
      PENDING: "#ff9800",
      SUBMITTED: "#2196f3",
    };
    return statusMap[status] || "#9e9e9e";
  };

  if (isLoading) {
    return (
      <DashboardWrapper
        title="GCA Profile"
        showBackBtn={true}
        backPath="/my-profile"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </DashboardWrapper>
    );
  }

  if (isError || !gcaData) {
    return (
      <DashboardWrapper title="GCA Profile" showBackBtn={true}>
        <Box {...styles("wrapper")}>
          <Typography color="error">
            Failed to load GCA details. Please try again.
          </Typography>
        </Box>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper
      title="GCA Profile"
      showBackBtn={true}
      customStyles={{
        children: {
          backgroundColor: "#f4f6f8",
          padding: "32px 24px",
        },
      }}
    >
      <Box {...styles("wrapper")}>
        {/* Header Section */}
        <Box {...styles("childWrapper")}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="20px"
            flexWrap="wrap"
          >
            <div>
              <Typography {...styles("heading")}>
                {GCATypeLabels[gcaData.gcaType] || gcaData.gcaType}
              </Typography>
              {/* <Box display="flex" gap="10px" marginTop="10px">
                <Chip
                  label={GCATypeLabels[gcaData.gcaType]}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={gcaData.status || "Unknown"}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(gcaData.status),
                    color: "white",
                  }}
                />
              </Box> */}
            </div>
            <Box {...styles("actionButtons")}>
              {!isEditing ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEditToggle}
                  >
                    Edit
                  </Button>
                  <Badge
                    color="error"
                    badgeContent={queriesUnreadCount}
                    max={99}
                    overlap="rectangular"
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setQueriesOpen(true)}
                    >
                      Queries
                    </Button>
                  </Badge>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSave}
                    disabled={
                      isSaving ||
                      repEmailCheck.checking ||
                      repEmailCheck.available === false
                    }
                  >
                    {isSaving
                      ? "Saving..."
                      : repEmailCheck.checking
                      ? "Checking email…"
                      : "Save Changes"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleEditToggle}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* GCA Data Section */}
        <Box {...styles("childWrapper")}>
          {renderGCATypeContent(
            isEditing && editData ? editData : gcaData,
            isEditing
          )}
        </Box>

        {/* Documents and Images Section */}
        <Box {...styles("childWrapper")}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {isEditing
                ? "Documents (Upload / Replace)"
                : "Uploaded Documents"}
            </Typography>

            {isEditing ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 2,
                }}
              >
                {supportedDocumentTypes.map((docType) => {
                  const existing = (editData?.documents || []).find(
                    (d: any) => d?.type === docType
                  );
                  const isUploading = uploadingDocType === docType;
                  return (
                    <Box
                      key={docType}
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        p: 2,
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            wordBreak: "break-word",
                          }}
                        >
                          {DocumentTypeLabels[docType] || docType}
                          <span style={{ color: "red", marginLeft: "4px" }}>
                            *
                          </span>
                        </Typography>
                        {existing?.frontUrl ? (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mb: 1 }}
                          >
                            <Box
                              component="span"
                              onClick={() => handleViewDocument(existing)}
                              sx={{
                                color: "#2196f3",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              View current file
                            </Box>
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mb: 1, color: "gray" }}
                          >
                            Not uploaded yet
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          component="label"
                          disabled={isUploading}
                        >
                          {isUploading
                            ? "Uploading..."
                            : existing?.frontUrl
                            ? "Replace"
                            : "Upload"}
                          <input
                            type="file"
                            hidden
                            accept="image/jpeg,image/png,image/jpg,application/pdf"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleDocumentUpload(docType, f);
                              e.target.value = "";
                            }}
                          />
                        </Button>
                      </Box>
                      {existing?.frontUrl && (
                        <Box
                          onClick={() => handleViewDocument(existing)}
                          sx={{ cursor: "pointer", flexShrink: 0 }}
                        >
                          <DocumentPreview
                            serialNo={existing.serialNo}
                            fallbackUrl={existing.frontUrl}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
                {supportedDocumentTypes.length === 0 && (
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    No documents are required for this entity type.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 2,
                }}
              >
                {Array.isArray(gcaData?.documents) &&
                gcaData.documents.length > 0 ? (
                  gcaData.documents.map((doc: any, idx: number) => (
                    <Box
                      key={doc.id || idx}
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        p: 2,
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                        "&:hover": { boxShadow: 2 },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            wordBreak: "break-word",
                          }}
                        >
                          {DocumentTypeLabels[doc.type] ||
                            doc.type ||
                            `Document ${idx + 1}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mb: 1,
                            color: "gray",
                            wordBreak: "break-all",
                          }}
                        >
                          {doc.serialNo}
                        </Typography>
                        {doc.frontUrl ? (
                          <>
                            <Box
                              component="span"
                              onClick={() => handleViewDocument(doc)}
                              sx={{
                                color: "#2196f3",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              View Front
                            </Box>
                            {doc.backUrl && (
                              <>
                                {" | "}
                                <Box
                                  component="span"
                                  onClick={() => handleViewBack(doc)}
                                  sx={{
                                    color: "#2196f3",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                >
                                  View Back
                                </Box>
                              </>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: "gray" }}>
                            File URL not available
                          </Typography>
                        )}
                      </Box>
                      {doc.frontUrl && (
                        <Box
                          onClick={() => handleViewDocument(doc)}
                          sx={{ cursor: "pointer", flexShrink: 0 }}
                        >
                          <DocumentPreview
                            serialNo={doc.serialNo}
                            fallbackUrl={doc.frontUrl}
                          />
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "gray", gridColumn: "1 / -1" }}
                  >
                    No documents uploaded yet
                  </Typography>
                )}
              </Box>
            )}

            {/* Images Grid - Front and Back */}
            {/* <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Document Images
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 2,
                }}
              >
                {Array.isArray(gcaData?.documents) &&
                gcaData.documents.length > 0 ? (
                  gcaData.documents
                    .map((doc: any, idx: number) => {
                      const images: {
                        url: string;
                        label: string;
                        docType: string;
                      }[] = [];
                      if (doc.frontUrl) {
                        images.push({
                          url: doc.frontUrl,
                          label: "Front",
                          docType: doc.type || "Document",
                        });
                      }
                      if (doc.backUrl) {
                        images.push({
                          url: doc.backUrl,
                          label: "Back",
                          docType: doc.type || "Document",
                        });
                      }
                      return images;
                    })
                    .flat()
                    .map((img: any, idx: number) => (
                      <Box
                        key={`${img.docType}-${img.label}-${idx}`}
                        sx={{
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          p: 1,
                          textAlign: "center",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={img.url}
                          alt={`${img.docType} - ${img.label}`}
                          style={{
                            width: "100%",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            marginBottom: "8px",
                          }}
                        />
                        <Typography variant="caption" sx={{ display: "block" }}>
                          {img.docType} - {img.label}
                        </Typography>
                      </Box>
                    ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "gray", gridColumn: "1 / -1" }}
                  >
                    No images uploaded
                  </Typography>
                )}
              </Box>
            </Box> */}
          </Box>
        </Box>

        {/* Update History Section */}
        <Box {...styles("childWrapper")}>
          <Typography {...styles("sectionTitle")}>Update History</Typography>
          {isHistoryLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : updateHistory && updateHistory.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Authorized Person
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Division ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Joining Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {updateHistory.map((history: any, idx: number) => (
                    <TableRow
                      key={history.id || idx}
                      sx={{ "&:hover": { backgroundColor: "#fafafa" } }}
                    >
                      <TableCell>
                        {new Date(history.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{history.name || "—"}</TableCell>
                      <TableCell>{history.email || "—"}</TableCell>
                      <TableCell>{history.phoneNumber || "—"}</TableCell>
                      <TableCell>{history.designation || "—"}</TableCell>
                      <TableCell>
                        {history.nameOfAuthorizedPerson || "—"}
                      </TableCell>
                      <TableCell>{history.divisionId || "—"}</TableCell>
                      <TableCell>
                        {history.joiningDate
                          ? new Date(history.joiningDate).toLocaleDateString()
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" sx={{ color: "gray", p: 2 }}>
              No update history available
            </Typography>
          )}
        </Box>
      </Box>

      <QueriesDialog open={queriesOpen} onClose={() => setQueriesOpen(false)} />

      <Snackbar
        open={!!showSnackbar.message}
        message={showSnackbar.message}
        severity={showSnackbar.severity}
        resetSnackbar={handleResetSnackbar}
      />

      <Dialog
        open={otpDialog.open}
        onClose={otpDialog.verifying ? undefined : handleCloseOtpDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Verify Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We sent a 6-digit OTP to <strong>{otpDialog.email}</strong>. Enter
            it below to confirm the email change and save your profile.
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter OTP"
            value={otpDialog.otp}
            onChange={(e) =>
              setOtpDialog((prev) => ({
                ...prev,
                otp: e.target.value.replace(/\D/g, "").slice(0, 6),
                error: "",
              }))
            }
            disabled={otpDialog.sending || otpDialog.verifying}
            inputProps={{ maxLength: 6, inputMode: "numeric" }}
          />
          {otpDialog.sending && (
            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
              Sending OTP...
            </Typography>
          )}
          {otpDialog.error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 1 }}
            >
              {otpDialog.error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleResendOtp}
            disabled={otpDialog.sending || otpDialog.verifying}
          >
            Resend OTP
          </Button>
          <Button
            onClick={handleCloseOtpDialog}
            disabled={otpDialog.verifying}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyOtpAndSave}
            variant="contained"
            color="primary"
            disabled={
              otpDialog.otp.length !== 6 ||
              otpDialog.sending ||
              otpDialog.verifying ||
              !otpDialog.otpToken
            }
          >
            {otpDialog.verifying ? "Verifying..." : "Verify & Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardWrapper>
  );
};

export default GCAView;
